import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { AudienceService } from 'app/library/audience/services/audience.service';
import {
    CreateValidScheduledStreamContentSuccess,
    FetchScheduledStreamContentsFailure,
    FetchScheduledStreamContentsSuccess,
    SearchScheduledStreamContents,
} from 'app/library/scheduled-stream-content/actions';
import { MaxBrainScheduledStreamContent } from 'app/library/scheduled-stream-content/models/scheduled-stream-content.model';
import { ScheduledStreamContentsState } from 'app/library/scheduled-stream-content/models/scheduled-stream-contents.state';
import { ScheduledStreamContentService } from 'app/library/scheduled-stream-content/services/scheduled-stream-content.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { IMaxBrainEnvironment } from 'app/projects/shared/src/lib/interfaces/environment.interface';
import * as moment from 'moment';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, startWith, takeUntil } from 'rxjs/operators';
import { EngageAddFeaturedContentDialogComponent } from '../engage-add-featured-content-dialog/engage-add-featured-content-dialog.component';
import { EngageScheduleFeaturedContentDialogComponent } from '../engage-schedule-featured-content-dialog/engage-schedule-featured-content-dialog.component';
import { PostingTypeApiService } from 'app/library/posting/services/posting-type.api-service';
import { PostingType } from 'app/library/posting/models/posting-type.model';
import { AudienceRelevantExperience } from 'app/library/grow/models/audience-relevant-experience.model';
import { LiveCategoryLinkApiService } from 'app/library/live-category-link/services/live-category-link.api-service';
import { LiveCategoryLink } from 'app/library/live-category-link/models/live-category-link.model';
@Component({
    selector: 'app-engage-edit-featured-content-dialog',
    templateUrl: './engage-edit-featured-content-dialog.component.html',
    styleUrls: ['./engage-edit-featured-content-dialog.component.scss'],
})
export class EngageEditFeaturedContentDialogComponent extends UnsubscribeOnDestroy implements OnInit, OnDestroy {
    @Select(ScheduledStreamContentsState.getFilteredEntities)
    filteredScheduledStreamContent$: Observable<MaxBrainScheduledStreamContent[]>;

    @Select(ScheduledStreamContentsState.getSearchTerm)
    scheduledStreamContentStateSearchTerm$: Observable<string>;

    @Select(ScheduledStreamContentsState.getTotalCount)
    scheduledStreamContentStateTotalCount$: Observable<number>;

    @Select(ScheduledStreamContentsState.isFetchingList)
    isFetchingScheduledStreamContenList$: Observable<boolean>;

    private _loadScheduledStreamContent$ = new BehaviorSubject<boolean>(null);
    loadScheduledStreamContent$ = this._loadScheduledStreamContent$.asObservable();

    engageAddFeaturedContentDialogRef: MatDialogRef<EngageAddFeaturedContentDialogComponent>;
    engageScheduleFeaturedContentDialogRef: MatDialogRef<EngageScheduleFeaturedContentDialogComponent>;

    @Input()
    featuredStreamContentId: string;

    @Input()
    widgetName: string;

    @Input()
    audienceId: string;

    @Input()
    audienceRelevantExperiences: AudienceRelevantExperience[] = [];

    isContentLoading = false;
    searchInput = new FormControl();
    unsplashViewsBaseUrl: string;

    postingTypeInput = new FormControl();
    postingTypes: PostingType[] = [];
    liveCategoryLinks: LiveCategoryLink[] = [];
    selectedPostingTypeId: string;
    isLiveCategoryLinksExpanded = false;
    isBaseContentEmpty = false;
    isResettingSearch = false;

    constructor(
        @Inject(MAXBRAIN_ENVIRONMENT) private _environment: IMaxBrainEnvironment,
        private _action$: Actions,
        public dialogRef: MatDialogRef<EngageEditFeaturedContentDialogComponent>,
        public audienceService: AudienceService,
        private _featuredStreamContentService: ScheduledStreamContentService,
        private _postingTypeApiService: PostingTypeApiService,
        private _matDialog: MatDialog,
        private _translateService: TranslateService,
        private _matSnackBar: MatSnackBar,
        private _store: Store,
        private _liveCategoryLinkApiService: LiveCategoryLinkApiService
    ) {
        super();
        this.unsplashViewsBaseUrl = this._environment.unsplashViewsBaseUrl;
    }

    async ngOnInit(): Promise<void> {
        this.postingTypes = await this._postingTypeApiService.getPostingTypes();
        this.liveCategoryLinks = await this._liveCategoryLinkApiService.getLiveCategoryLinks(this.audienceId, this.featuredStreamContentId).toPromise();

        this._action$
            .pipe(ofActionDispatched(CreateValidScheduledStreamContentSuccess), takeUntil(this._unsubscribeAll))
            .subscribe((action: CreateValidScheduledStreamContentSuccess) => {
                const message = this._translateService.instant('ENGAGE.SCHEDULED_STREAM_CONTENT.MESSAGE.SCHEDULE_SUCCESS');

                this._matSnackBar.open(message, 'OK', {
                    verticalPosition: 'top',
                    duration: 1000,
                });
            });

        this.postingTypeInput.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe((postingTypeId: string) => {
            this.selectedPostingTypeId = postingTypeId;
            setTimeout(() => {
                this._loadScheduledStreamContent$.next(true);
            });
        });

        const searchValueChanges$ = this.searchInput.valueChanges.pipe(takeUntil(this._unsubscribeAll), distinctUntilChanged());

        searchValueChanges$
            .pipe(filter((searchValue: string) => !(searchValue || '').trim()))
            .subscribe((searchValue: string) => {
                // Clear should trigger immediate refetch so UI never flashes no-content between states.
                this.isResettingSearch = true;
                this.isBaseContentEmpty = false;
                this._store.dispatch(new SearchScheduledStreamContents(searchValue));
            });

        searchValueChanges$
            .pipe(
                filter((searchValue: string) => !!(searchValue || '').trim()),
                debounceTime(300)
            )
            .subscribe((searchValue: string) => {
                this.isResettingSearch = false;
                this._store.dispatch(new SearchScheduledStreamContents(searchValue));
            });

        this._action$
            .pipe(ofActionDispatched(FetchScheduledStreamContentsSuccess, FetchScheduledStreamContentsFailure), takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this.isResettingSearch = false;
            });

        combineLatest([
            this.scheduledStreamContentStateTotalCount$,
            this.isFetchingScheduledStreamContenList$,
            this.searchInput.valueChanges.pipe(startWith(this.searchInput.value)),
            this.postingTypeInput.valueChanges.pipe(startWith(this.postingTypeInput.value)),
        ])
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(([totalCount, isFetching, searchValue, postingTypeId]: [number, boolean, string, string]) => {
                const hasSearchTerm = !!(searchValue || '').trim();
                const hasPostingTypeFilter = !!postingTypeId;

                // No-content state should only reflect the baseline list without active search/filter.
                if (!isFetching && !hasSearchTerm && !hasPostingTypeFilter) {
                    this.isBaseContentEmpty = totalCount === 0;
                }
            });
    }

    toggleLiveCategoryLinks(): void {
        this.isLiveCategoryLinksExpanded = !this.isLiveCategoryLinksExpanded;
    }

    async onDeleteLiveCategoryLink(liveCategoryLink: LiveCategoryLink): Promise<void> {
        await this._liveCategoryLinkApiService.removeLiveCategoryLink(liveCategoryLink.id).toPromise();
        this.liveCategoryLinks = this.liveCategoryLinks.filter((lcl) => lcl.id !== liveCategoryLink.id);
        this._loadScheduledStreamContent$.next(true);
    }

    openAddFeaturedContentDialog(): void {
        this.engageAddFeaturedContentDialogRef = this._matDialog.open(EngageAddFeaturedContentDialogComponent, {
            panelClass: 'engage-add-featured-content-dialog',
            disableClose: false,
            width: '991px',
            autoFocus: false,
        });
        this.engageAddFeaturedContentDialogRef.componentInstance.audienceId = this.audienceId;
        this.engageAddFeaturedContentDialogRef.componentInstance.featuredStreamContentId = this.featuredStreamContentId;
        this.engageAddFeaturedContentDialogRef.componentInstance.audienceRelevantExperiences = this.audienceRelevantExperiences;
        this.engageAddFeaturedContentDialogRef.componentInstance.liveCategoryLinks = this.liveCategoryLinks;
        this.engageAddFeaturedContentDialogRef.componentInstance.engageEditFeaturedContentDialogComponent = this.dialogRef;

        this.engageAddFeaturedContentDialogRef.afterClosed().subscribe(async () => {
            this._featuredStreamContentService.fetchScheduledStreamContents(this.audienceId, this.featuredStreamContentId, 5, 1);
            this.liveCategoryLinks = await this._liveCategoryLinkApiService.getLiveCategoryLinks(this.audienceId, this.featuredStreamContentId).toPromise();
        });
    }

    openScheduleFeaturedContentDialog(scss: MaxBrainScheduledStreamContent): void {
        this.engageScheduleFeaturedContentDialogRef = this._matDialog.open(EngageScheduleFeaturedContentDialogComponent, {
            disableClose: false,
            width: '648px',
            autoFocus: false,
        });
        // if validFrom is in the past, we set today's date as the date that is valid
        const minDate = moment().isAfter(scss.posting.validFrom) ? moment().toString() : scss.posting.validFrom;
        this.engageScheduleFeaturedContentDialogRef.componentInstance.postId = scss.id;
        this.engageScheduleFeaturedContentDialogRef.componentInstance.postName = scss.posting.translations[0].title;
        this.engageScheduleFeaturedContentDialogRef.componentInstance.minDate = new Date(minDate);
        this.engageScheduleFeaturedContentDialogRef.componentInstance.maxDate = scss.posting.validTo ? new Date(scss.posting.validTo) : null;
        this.engageScheduleFeaturedContentDialogRef.componentInstance.scss = scss;

        this.engageScheduleFeaturedContentDialogRef.afterClosed().subscribe(async (result) => {
            if (result && result.data) {
                const data: MaxBrainScheduledStreamContent = {
                    id: scss.id,
                    audience: { id: this.audienceId },
                    posting: { id: scss.posting.id },
                    featuredStream: { id: this.featuredStreamContentId },
                    startDate: result.data.startDate || null,
                    endDate: result.data.endDate || null,
                    priority: result.data.priority || null,
                } as MaxBrainScheduledStreamContent;
                await this._featuredStreamContentService.updateScheduledStreamContent(data);

                this._featuredStreamContentService.fetchScheduledStreamContents(this.audienceId, this.featuredStreamContentId, 100, 1);
            }

            this.engageScheduleFeaturedContentDialogRef = null;
        });
    }

    ngOnDestroy(): void {
        this._store.dispatch(new SearchScheduledStreamContents(''));
        super.ngOnDestroy();
    }
}
